import React from "react";
import * as Autosuggest from "react-autosuggest";
import { FaDownload, FaSearch, FaTimes } from "react-icons/fa";
import styled from "styled-components";

import ClickableElement, { ClickableLink } from "../../../components/utils/ClickableElement";
import ToggleDisplay from "../../../components/utils/ToggleDisplay";
import bp from "../../../styles/breakpoints";

export const SettingsFormDiv = styled.div`
  display: block;
  text-align: left;
`;

export const StyledSelect = styled.select`
  background-color: ${({ theme }) => theme.colors.lightgrey2};
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: bold;
  height: 4.6rem;
  padding: 1rem;
  text-transform: uppercase;
  width: 30%;
`;
export const FormSelect = styled(StyledSelect)`
  margin-top: 1rem;
  border-radius: 0.5rem;
  font-size: ${({ theme }) => theme.fontSizes.regular};
  color: ${({ theme }) => theme.colors.darkblue};
`;
export const SettingsInputItem = styled.div`
  align-items: end;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: 1rem;
  margin-bottom: 1rem;

  label,
  input {
    cursor: pointer;
  }

  & > * {
    display: flex;
    align-items: center;
  }
`;

export const CheckboxDescription = styled.span`
  margin-left: 0.5em;
  font-weight: 600;
  cursor: pointer;
`;

export const ItemTitle = styled.h1`
  font-size: inherit;
  font-weight: inherit;
  margin: inherit;
`;

export const ItemLabel = styled.label`
  font-size: inherit;
  font-weight: inherit;
  margin: inherit;
`;

export const HintText = styled.span`
  color: ${({ theme }) => theme.colors.grey};
  font-family: ${({ theme }) => theme.fonts.OpenSans};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 400;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

export const ClickableToggleDisplay = styled(ToggleDisplay)`
  cursor: pointer;

  &:hover {
    outline: 0.1rem dotted black;
  }

  .public-DraftStyleDefault-block {
    margin: 0;
  }

  .public-DraftEditorPlaceholder-inner {
    font-style: italic;
  }

  @media (${bp.min800}) {
    min-height: 5rem;
    min-width: 75rem;
    outline-color: white;
    outline-width: 0rem;
    transition: outline-color 0.5s;
    width: 75rem;
    width: stretch;
  }
`;

export const FieldSet = styled.fieldset`
  background-color: ${({ theme }) => theme.colors.lightgrey3};
  border: none;
  margin: 0 0.2rem;

  legend {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 0;
    padding: 0;
  }
`;

interface IFieldSetToggleDisplayProps {
  onClick: () => void;
  legend: string;
  shouldDisplay: boolean;
}

export const FieldSetToggleDisplay: React.FC<IFieldSetToggleDisplayProps> = props => {
  const { onClick, shouldDisplay } = props;
  return (
    <ClickableToggleDisplay onClick={onClick} shouldDisplay={shouldDisplay}>
      <FieldSet>
        <legend>{props.legend}</legend>
        {props.children}
      </FieldSet>
    </ClickableToggleDisplay>
  );
};

interface ISearchFieldProps {
  legend: string;
}

export const SearchField: React.FC<ISearchFieldProps> = props => {
  return (
    <FieldSet>
      <legend>{props.legend}</legend>
      {props.children}
    </FieldSet>
  );
};

interface IClearFieldProps {
  children: React.ReactNode;
  inputRef: React.RefObject<HTMLInputElement>;
}

export const ClearField = (props: IClearFieldProps) => {
  const handleClear = () => props.inputRef.current && (props.inputRef.current.value = "");
  return (
    <ClearInputContainer>
      {props.children}
      <InputCrossIcon onClick={handleClear} />
    </ClearInputContainer>
  );
};

const ClearInputContainer = styled.div`
  position: relative;
  padding-right: 2rem;
`;

const InputCrossIcon = styled(FaTimes)`
  position: absolute;
  right: 0;
  top: 7px;
  cursor: pointer;
  font-size: 2rem;
`;

export const SetupButtonLink = styled(ClickableLink)`
  button {
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.colors.buttonBlue};
    box-shadow: 0.2rem 0.2rem 0.5rem 0 rgba(0, 0, 0, 0.3);
    color: ${({ theme }) => theme.colors.white};
    display: flex;
    justify-content: space-around;
    padding: 0.5rem;
    margin-bottom: 0.5rem;

    &:hover {
      opacity: 0.7;
    }
  }
`;

export const SetupButton = styled(ClickableElement).attrs(props => ({
  disabled: props.disabled,
}))`
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.buttonBlue};
  box-shadow: 0.2rem 0.2rem 0.5rem 0 rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.white};
  padding: 1rem;
  font-weight: bold;
  font-size: 1.2rem;

  :hover {
    opacity: 0.7;
  }

  :disabled {
    background-color: ${({ theme }) => theme.colors.lightgrey};
    color: ${({ theme }) => theme.colors.darkgrey3};
    cursor: not-allowed;
  }
`;

export const ResetButton = styled(SetupButton)`
  background-color: ${({ theme }) => theme.colors.lightgrey};
  color: ${({ theme }) => theme.colors.black};
`;

export const DeleteButton = styled(SetupButton)`
  display: block;
  background-color: red;
  color: ${({ theme }) => theme.colors.white};
`;

export const WarnButton = styled(SetupButton)`
  display: block;
  background-color: ${({ theme }) => theme.colors.orangeWarning};
  color: ${({ theme }) => theme.colors.white};
`;

export const AddButton = styled(SetupButton)`
  display: block;
  text-align: center;

  :disabled {
    background-color: ${({ theme }) => theme.colors.lightgrey};
    color: ${({ theme }) => theme.colors.lightgrey2};
    opacity: 1;
  }
`;

interface IButtonRow {
  margin: number;
  width: number;
}

export const ButtonRow = styled.div<IButtonRow>`
  margin: ${props => props.margin}rem 0;
  width: ${props => props.width}rem;
`;

export const ButtonsRow = styled(ButtonRow)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    width: ${({ width }) => width}rem;
  }
`;

export const FooterNote = styled.i`
  margin: 2rem 0;
  font-size: ${({ theme }) => theme.fontSizes.smallText};
  font-weight: 600;
`;

export const ErrorHeader = styled.span`
  color: red;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSizes.regular};
`;

export const CustomSearchInputContainer = styled.div`
  display: flex;
  direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;

  .react-autosuggest__input {
    border: none;
    background-color: inherit;
    margin-right: 1rem;
    padding: 1rem 0.5rem;
    width: 100%;

    :: placeholder {
      font-style: italic;
    }

    :hover {
      border-bottom: 0.1rem solid black;
    }
  }
`;

export function CustomSearchInput(props: Autosuggest.RenderInputComponentProps): React.ReactNode {
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    props.onChange?.(event);
  };
  // NOTE: style={{}} is necessary to overridde the default inline to overridde
  // the default inputProps in Autocomplete.
  return (
    <CustomSearchInputContainer>
      <input {...props} onChange={onChangeHandler} style={{}} />
      <FaSearch />
    </CustomSearchInputContainer>
  );
}

export const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.grey};
  font-size: ${({ theme }) => theme.fontSizes.large};
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  width: calc(100% - 1.2rem);
  outline: none;

  ::placeholder {
    font-style: italic;
    color: ${({ theme }) => theme.colors.lightgrey4};
  }
`;

export const InputLink = styled(Input)`
  border: none;
  background-color: inherit;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.lightgrey2};
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.darkblue};
`;

const FileInputContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.lightgrey3};
  display: flex;
  direction: row;
  padding: 1rem;
  margin-top: 2rem;
`;

const FileInputButton = styled.button`
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.2rem solid ${({ theme }) => theme.colors.buttonBlue};
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.colors.buttonBlue};
  font-weight: bold;
  margin-right: 1.5rem;
  padding: 0.5rem;

  :hover {
    opacity: 0.7;
  }
`;

interface ICustomFileInputProps {
  file: File | null;
  onClickFunc: () => void;
}

export const CustomFileInput: React.FC<ICustomFileInputProps> = ({ file, onClickFunc }) => (
  <FileInputContainer>
    <FileInputButton onClick={onClickFunc}>Choose file</FileInputButton>
    <span>{file?.name ?? "Select a file"}</span>
  </FileInputContainer>
);

interface IDownloadFileButtonProps {
  text: string;
  downloadUrl: () => string;
  width?: number;
  margin?: number;
}

export const DownloadFileButton: React.FC<IDownloadFileButtonProps> = ({
  text,
  downloadUrl,
  width = 30,
  margin = 1,
}) => (
  <ButtonRow width={width} margin={margin}>
    <SetupButtonLink download={true} href={downloadUrl()}>
      <span>
        <FaDownload /> {text}
      </span>
    </SetupButtonLink>
  </ButtonRow>
);
